import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useNotifications } from 'app/application/application'
import { useUser, useWorkspace } from 'app/user/application'
import { WorkspaceDeliveries } from 'app/user/types'

import DashboardCard from '../DashboardCard'
import styles from './Delivery.module.scss'

const Delivery = () => {
  const chartId = useId()
  const gradientId = `${chartId}-gradient`
  const stripeGradientId = `${chartId}-stripe-gradient`
  const patternId = `${chartId}-stripes`
  const { createNotification, te } = useNotifications()
  const { t, i18n } = useTranslation('dashboard')
  const { workspace } = useUser()
  const { getDeliveries } = useWorkspace()

  const [deliveries, setDeliveries] = useState<WorkspaceDeliveries>()
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const now = new Date()
  const year = now.getUTCFullYear()
  const month = now.getUTCMonth()
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`
  const formatDate = (day: number) =>
    new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month, day)))

  useEffect(() => {
    let active = true
    setDeliveries(undefined)
    setState('loading')
    if (!workspace?.id) return
    void getDeliveries().then((result) => {
      if (!active) return
      if (result.ok) {
        setDeliveries(result.val)
        setState('ready')
      } else {
        setState('error')
        createNotification({
          title: te(`http.${result.val.code}`),
          message: result.val.message,
          type: 'error',
        })
      }
    })
    return () => {
      active = false
    }
  }, [createNotification, getDeliveries, te, workspace?.id, monthPrefix])

  const data = deliveries?.days
    .filter((day) => day.date.startsWith(monthPrefix))
    .map((day) => ({
      day: Number(day.date.slice(8, 10)),
      percentage:
        day.successful + day.failed > 0
          ? (day.successful / (day.successful + day.failed)) * 100
          : 0,
    }))
    .sort((a, b) => a.day - b.day)
  const successPercentage = deliveries?.meta.successPercentage ?? 0

  return (
    <DashboardCard title={t('tiles.delivery.title')}>
      <div className={styles.delivery} aria-busy={state === 'loading'}>
        {deliveries ? (
          <>
            <div className={styles.summary}>
              <strong>
                {successPercentage.toLocaleString(i18n.language, { maximumFractionDigits: 1 })}
                <span>%</span>
              </strong>
              <p>
                {t('tiles.delivery.summary', {
                  failed: deliveries.meta.failed,
                  total: deliveries.meta.total,
                  count: deliveries.meta.connections,
                })}
              </p>
            </div>
            <div className={styles.chart}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop className={styles.fillStop} offset="0%" stopOpacity={0.5} />
                      <stop className={styles.fillStop} offset="100%" stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id={stripeGradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop className={styles.stripeStop} offset="0%" stopOpacity={0.45} />
                      <stop className={styles.stripeStop} offset="100%" stopOpacity={0.08} />
                    </linearGradient>
                    <pattern id={patternId} width={5} height="100%" patternUnits="userSpaceOnUse">
                      <rect width={1} height="100%" fill={`url(#${stripeGradientId})`} />
                    </pattern>
                  </defs>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    type="number"
                    dataKey="day"
                    domain={[1, daysInMonth]}
                    ticks={[1, 8, 15, 22, daysInMonth]}
                    tickFormatter={formatDate}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={12}
                  />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                    interval={0}
                    tickFormatter={(value: number) => `${value}%`}
                    axisLine={false}
                    tickLine={false}
                    width={38}
                  />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className={styles.tooltip}>
                          <p className={styles.tooltipDate}>{formatDate(Number(label))}</p>
                          <div className={styles.tooltipRow}>
                            <span>{t('tiles.delivery.successfulUploads')}</span>
                            <span className={styles.tooltipValue}>
                              {Number(payload[0].value).toLocaleString(i18n.language, {
                                maximumFractionDigits: 1,
                              })}
                              %
                            </span>
                          </div>
                        </div>
                      ) : null
                    }
                  />
                  <Area
                    className={styles.area}
                    type="monotone"
                    dataKey="percentage"
                    fill={`url(#${gradientId})`}
                    fillOpacity={1}
                    isAnimationActive={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="percentage"
                    fill={`url(#${patternId})`}
                    fillOpacity={1}
                    stroke="none"
                    activeDot={false}
                    tooltipType="none"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <p className={styles.message}>{t(`tiles.delivery.${state}`)}</p>
        )}
      </div>
    </DashboardCard>
  )
}

export default Delivery
